import { BlobReader, ZipReader } from '@zip.js/zip.js';

export const MAX_SET_BYTES = 4 * 1024 ** 3;
export const MAX_ZIP_BYTES = 2 * 1024 ** 3;
export const MAX_SET_FILES = 10000;
export const isCineFile = (name: string) => /\.(avi|mp4|mov)$/i.test(name);
export const isStudyAttachment = (name: string) => /\.(jpe?g|pdf)$/i.test(name);
export const memberLimit = (name: string) => (isStudyAttachment(name) ? 32 : isCineFile(name) ? 256 : 128) * 1024 ** 2;
export function ignoredMember(name: string) {
  return (
    name.split(/[\\/]/).some((part) => part.startsWith('.') || part === '__MACOSX') || /(^|\/)DICOMDIR$/i.test(name)
  );
}
/** Expand one member at a time. Await durable storage before decompressing the next member. */
export async function queueUploadSet(
  files: File[],
  add: (file: File) => Promise<void>,
  progress: (message: string) => void,
) {
  let count = 0,
    skipped = 0,
    total = 0;
  async function accept(file: File, name: string) {
    if (ignoredMember(name)) {
      skipped++;
      return;
    }
    validateMember(name, file.size);
    if (++count > MAX_SET_FILES || (total += file.size) > MAX_SET_BYTES)
      throw Error('Study set exceeds 10,000 files or 4 GiB expanded');
    progress(`Preparing file ${count}: ${name}`);
    await add(new File([file], name, { type: file.type }));
  }
  for (const file of files) {
    if (!/\.zip$/i.test(file.name)) {
      await accept(file, file.webkitRelativePath || file.name);
      continue;
    }
    if (file.size > MAX_ZIP_BYTES) throw Error('ZIP limit is 2 GiB. Use folder selection for a larger export.');
    const reader = new ZipReader(new BlobReader(file));
    try {
      let entries = 0;
      for await (const entry of reader.getEntriesGenerator()) {
        if (++entries > MAX_SET_FILES) throw Error('ZIP contains too many entries (maximum 10,000)');
        if (entry.directory || ignoredMember(entry.filename)) {
          skipped++;
          continue;
        }
        validateMember(entry.filename, entry.uncompressedSize);
        if (entry.encrypted) throw Error('Password-protected ZIP files are not supported');
        if (total + entry.uncompressedSize > MAX_SET_BYTES) throw Error('Study set exceeds 4 GiB expanded');
        const chunks: Uint8Array<ArrayBuffer>[] = [];
        let size = 0;
        await entry.getData(
          new WritableStream<Uint8Array>({
            write(chunk) {
              size += chunk.byteLength;
              if (size > memberLimit(entry.filename) || total + size > MAX_SET_BYTES)
                throw Error('Expanded ZIP member exceeds its size limit');
              chunks.push(new Uint8Array(chunk));
            },
          }),
          { checkSignature: true, useWebWorkers: false },
        );
        await accept(new File(chunks, entry.filename), entry.filename);
      }
    } finally {
      await reader.close();
    }
  }
  return { count, skipped };
}

export function validateMember(name: string, bytes: number) {
  if (!name || name.startsWith('/') || name.includes('\\') || name.split('/').includes('..') || /^[a-z]:/i.test(name))
    throw Error('ZIP contains an unsafe file path');
  if (/\.zip$/i.test(name)) throw Error('Nested ZIP files are not supported. Select the imaging export ZIP itself.');
  if (bytes > memberLimit(name))
    throw Error(`${name}: exceeds the individual file limit (32 MiB JPG/PDF / 128 MiB DICOM / 256 MiB cine)`);
}
